import { ReactNode } from 'react';
import { useComponents } from '../../context/site-content.context';
import { ContentWrapper } from './ContentWrapper';
import { AlertVariantDisplay } from '../variants/AlertVariantDisplay';
import { AlternateVariantDisplay } from '../variants/AlternateVariantDisplay';
import { BannerVariantDisplay } from '../variants/BannerVariant';
import { ColumnVariantDisplay } from '../variants/ColumnVariantDisplay';
import { DocumentVariantDisplay } from '../variants/DocumentDisplay';
import { GridVariantDisplay } from '../variants/GridVariantDisplay';

interface Props {
    collectionId: string;
    contentClassName?: string;
}

export const CollectionDisplay = ({
    collectionId,
    contentClassName,
}: Props): React.ReactElement<any, any> | null => {
    const components = useComponents(collectionId);

    return (
        <>
            {components.map((item, index) => {
                // alerts are special, they are injected into the content wrapper of the
                // next section (if it exists)
                // this seems super messy, but I'm not sure how else to handle this case
                let alertVariant: ReactNode = undefined;
                const previousItem = index !== 0 && components[index - 1];
                if (previousItem && previousItem.variant === 'alert') {
                    alertVariant = (
                        <AlertVariantDisplay item={previousItem} variantIndex={index - 1} />
                    );
                }

                if (item.variant === 'alert') {
                    // if it's the last item in the list
                    if (index === components.length - 1) {
                        return (
                            <ContentWrapper
                                key={index}
                                contentClassName={
                                    contentClassName ? `p-5 ${contentClassName}` : 'p-5'
                                }
                            >
                                {alertVariant}
                                <AlertVariantDisplay item={item} variantIndex={index} />
                            </ContentWrapper>
                        );
                    }

                    // if the previous item was also a alert just send it through
                    if (alertVariant) {
                        <ContentWrapper
                            key={index}
                            contentClassName={contentClassName ? `p-5 ${contentClassName}` : 'p-5'}
                        >
                            {alertVariant}
                        </ContentWrapper>;
                    }
                }

                switch (item.variant) {
                    case 'alternate':
                        return (
                            <AlternateVariantDisplay
                                before={alertVariant}
                                key={index}
                                item={item}
                                contentClassName={contentClassName}
                            />
                        );
                    case 'grid':
                        return (
                            <ContentWrapper key={index} contentClassName={contentClassName}>
                                <GridVariantDisplay item={item} before={alertVariant} />
                            </ContentWrapper>
                        );
                    case 'document':
                        return (
                            <DocumentVariantDisplay
                                before={alertVariant}
                                item={item}
                                key={index}
                                contentClassName={contentClassName}
                            />
                        );
                    case 'column':
                        return (
                            <ColumnVariantDisplay
                                before={alertVariant}
                                item={item}
                                key={index}
                                contentClassName={contentClassName}
                            />
                        );
                    case 'banner':
                        return (
                            <BannerVariantDisplay
                                before={alertVariant}
                                item={item}
                                key={index}
                                contentClassName={contentClassName}
                            />
                        );
                    // alerts are handled at the start
                    case 'alert':
                        return null;
                    default:
                        return null;
                }
            })}
        </>
    );
};
